import {LineString, Point, Polygon, MultiLineString} from 'geojson';
import * as React from 'react';

import { connect } from 'react-redux';
import WKTParser from 'terraformer-wkt-parser';
import * as THREE from 'three';

import datafangstHelpers from '../../../../utilities';
import { DisplayFeature, DisplayFeatureType, Ground3dData, SceneData } from '../../contract-features-3/domain';
import { getBounds } from '../utils/bounds';
import FeatureStyler from '../utils/FeatureStyler';
import {ReactNode} from "react";
import FeaturesState from "../../contract-features-3/domain/FeaturesState";

interface TagProps {
    featureTypeColors: { [id: number]: string },
    systemCenter: [number, number]
}

interface StateProps {
    selectedFeatureType: DisplayFeatureType,
    additionalSelectedFeatureTypes: DisplayFeatureType[],
    selectedFeatures: string[],
    sceneData: SceneData,
    ground3dData: Ground3dData
}

type SceneHandlerProps = TagProps & StateProps

interface SceneHandlerState {
}

class SceneHandler extends React.Component<SceneHandlerProps, SceneHandlerState> {

    private systemCenter: [number, number];

    componentWillReceiveProps(nextProps: SceneHandlerProps) {
        if (nextProps.systemCenter != undefined) {
            this.systemCenter = nextProps.systemCenter;
        }

        if (this.props.selectedFeatureType == undefined || nextProps.ground3dData !== this.props.ground3dData) {
            SceneHandler.redrawGround(nextProps);
        }

        if (nextProps.selectedFeatureType != undefined &&
            (this.props.selectedFeatureType == undefined || this.props.selectedFeatureType.typeId !== nextProps.selectedFeatureType.typeId)) {
            this.drawSelectedFeatureType(nextProps);
        }

        this.redrawAdditionalSelectedFeatureTypes(nextProps);
    }

    render(): ReactNode {
        return null;
    }

    private drawSelectedFeatureType(nextProps: SceneHandlerProps) {
        // Remove all old features
        nextProps.sceneData.scene.children = nextProps.sceneData.scene.children.filter((child) => !child.userData['isFeature']);

        // Draw the new main feature type
        let color: string;
        for (let feature of nextProps.selectedFeatureType.features) {
            color = nextProps.featureTypeColors[feature.typeId];
            this.drawFeature(feature, color, true);
        }
    }

    private redrawAdditionalSelectedFeatureTypes(nextProps: SceneHandlerProps) {
        // Remove deselected feature types
        const removedFeatureTypes = this.props.additionalSelectedFeatureTypes.filter((featureType) => {
            return nextProps.additionalSelectedFeatureTypes.indexOf(featureType) < 0;
        });
        for (let featureType of removedFeatureTypes) {
            nextProps.sceneData.scene.children = nextProps.sceneData.scene.children.filter((child) => {
                return child.userData['typeId'] !== featureType.typeId;
            });
        }

        // Add selected feature types
        if (nextProps.additionalSelectedFeatureTypes.length > 0) {
            const addedFeatureTypes = nextProps.additionalSelectedFeatureTypes.filter((featureType) => {
                return this.props.additionalSelectedFeatureTypes.indexOf(featureType) < 0;
            });
            let color: string;
            for (let featureType of addedFeatureTypes) {
                for (let feature of featureType.features) {
                    color = nextProps.featureTypeColors[feature.typeId];
                    this.drawFeature(feature, color, false);
                }
            }
        }
    }

    private drawFeature(feature: DisplayFeature, color = '#000000', isSelectedFeatureType = false) {
        const geometryData = WKTParser.parse(feature.geometry.wkt);
        switch (geometryData.type) {
        case 'Point':
            this.drawPoint(feature, geometryData as Point, color, isSelectedFeatureType);
            break;
        case 'LineString':
            this.drawLineString(feature, geometryData as LineString, color, isSelectedFeatureType);
            break;
        case 'Polygon':
            this.drawPolygon(feature, geometryData as Polygon, color, isSelectedFeatureType);
            break;
            case 'MultiLineString':
            this.drawMultiLineString(feature, geometryData as MultiLineString, color, isSelectedFeatureType);
            break;
        default:
            throw new Error(`Unhandled geometry type: ${geometryData.type}`)
        }
    }

    private drawPoint(feature: DisplayFeature, geometryData: Point, color: string, isSelectedFeatureType: boolean) {
        // Calculations
        const centerPoint = geometryData.coordinates;
        const projectedCenterPoint = this.projectPoint(centerPoint);

        // Stage geometries
        const geometry = new THREE.SphereGeometry(1, 16, 12);

        // Stage materials
        const materialProperties = {
            color: color,
            transparent: !isSelectedFeatureType,
            opacity: isSelectedFeatureType ? 1 : 0.5
        };

        const material = new THREE.MeshLambertMaterial(materialProperties);

        // Create meshes
        const mesh = new THREE.Mesh(geometry, material);

        // Set position
        mesh.position.fromArray(projectedCenterPoint);

        // Add to scene
        this.addMeshToScene(feature, color, isSelectedFeatureType, mesh, 'Point');
    }

    private drawLineString(feature: DisplayFeature,
                           geometryData: LineString,
                           color: string,
                           isSelectedFeatureType: boolean,
                           geometryType = 'LineString') {
        // Calculations
        const bounds = getBounds(geometryData.coordinates);
        const centerPoint = [
            (bounds.maxX + bounds.minX)*0.5,
            (bounds.maxY + bounds.minY)*0.5,
            (bounds.maxZ + bounds.minZ)*0.5
        ];
        const points: THREE.Vector3[] = [];
        let projectedPoint: number[] = [];
        for (let point of geometryData.coordinates) {
            projectedPoint[0] = point[0] - centerPoint[0];
            projectedPoint[1] = point[1] - centerPoint[1];
            projectedPoint[2] = (isNaN(point[2]) ? 0 : point[2]) - centerPoint[2];
            points.push((new THREE.Vector3()).fromArray(projectedPoint));
        }
        const projectedCenterPoint = this.projectPoint(centerPoint);

        const primitiveGeometry = new THREE.Geometry();
        primitiveGeometry.vertices = points;

        // Stage materials
        const materialProperties = {
            color: color,
            transparent: !isSelectedFeatureType,
            opacity: isSelectedFeatureType ? 1 : 0.5
        };

        const primitiveMaterial = new THREE.LineBasicMaterial(Object.assign(materialProperties, {
            linewidth: geometryType === 'Polygon' ? 5 : 3
        }));

        // Create meshes
        const primitiveMesh = new THREE.Line(primitiveGeometry, primitiveMaterial);

        // Set position
        primitiveMesh.position.fromArray(projectedCenterPoint);

        // Add to scene
        this.addMeshToScene(feature, color, isSelectedFeatureType, primitiveMesh, geometryType);
    }

    private drawMultiLineString(feature: DisplayFeature, geometryData: MultiLineString, color: string, isSelectedFeatureType: boolean) {
        for (let lineStringCoordinates of geometryData.coordinates) {
            const lineStringGeometry: LineString = {
                type: 'LineString',
                coordinates: lineStringCoordinates
            };
            this.drawLineString(feature, lineStringGeometry, color, isSelectedFeatureType);
        }
    }

    /**
     * TODO
     * Polygons are now treated as series of LineStrings, with slightly wider lines.
     * Polygons should ideally be filled (solid). This is a hard problem, see JIRA-issue NVDB-2450.
     */
    private drawPolygon(feature: DisplayFeature, geometryData: Polygon, color: string, isSelectedFeatureType: boolean) {
        for (let lineStringCoordinates of geometryData.coordinates) {
            const lineStringGeometry: LineString = {
                type: 'LineString',
                coordinates: lineStringCoordinates
            };
            this.drawLineString(feature, lineStringGeometry, color, isSelectedFeatureType, 'Polygon');
        }
    }

    private addMeshToScene(feature: DisplayFeature,
                           color: string,
                           isSelectedFeatureType: boolean,
                           mesh: THREE.Mesh | THREE.Points | THREE.Line,
                           geometryType: string) {
        // Rotates around X-axis to switch from OpenGL's Z is "into the camera" view to mathematical Z is "upwards"
        mesh.rotateX(datafangstHelpers.degreesToRadians(-90));

        mesh.name = feature.id;
        mesh.userData = {
            color: color,
            isFeature: true,
            isSelectedFeatureType: isSelectedFeatureType,
            typeId: feature.typeId,
            geometryType: geometryType
        };

        FeatureStyler.styleUnselected(mesh);

        this.props.sceneData.scene.add(mesh);
    }

    /**
     * Projects any point from wgs84 coordinates (given by wkt) to the coordinate system of Three.js, as well as moving
     * the point to the system center, based on the bounds of the selected feature.
     * Also corrects for geometries where only X and Y coordinates are present.
     *
     * @param {number[]} point Is given as [?, ?], which is X and Y, or as [?, ?, ?], being X, Y, and Z.
     * @returns {number[]} The projected point, same rules about Z coordinate as above.
     */
    private projectPoint(point: number[]): number[] {
        const translatedX = point[0] - this.systemCenter[0];
        const translatedY = point[1] - this.systemCenter[1];
        const translatedZ = point[2];

        const projectedX = translatedX;
        const projectedY = translatedZ;
        const projectedZ = -translatedY;

        const missingZ = isNaN(projectedZ) ? 0 : projectedZ;

        return [projectedX, projectedY, missingZ];
    }

    private static redrawGround(props: SceneHandlerProps) {
        props.sceneData.scene.children = props.sceneData.scene.children.filter((c) => c.name !== 'grid');

        if (props.ground3dData.show) {
            const sizeInMeters = 100000;
            const squareSizeInMeters = props.ground3dData.squareSize;
            const color = 0x888888;
            const groundGrid = new THREE.GridHelper(sizeInMeters, sizeInMeters/squareSizeInMeters, color, color);
            groundGrid.position.setY(props.ground3dData.height);
            groundGrid.name = 'grid';

            props.sceneData.scene.add(groundGrid);

            if (props.ground3dData.filled) {
                const planeGeometry = new THREE.PlaneGeometry(sizeInMeters, sizeInMeters);
                const planeMaterial = new THREE.MeshBasicMaterial({
                    color: 0xbbbbbb,
                    opacity: 0.8,
                    transparent: true,
                    side: THREE.DoubleSide
                });
                const groundGridPlane = new THREE.Mesh(planeGeometry, planeMaterial);
                groundGridPlane.renderOrder = 2;
                // Rotates around X-axis to switch from OpenGL's Z is "into the camera" view to mathematical Z is "upwards"
                groundGridPlane.rotateX(datafangstHelpers.degreesToRadians(-90));
                groundGridPlane.position.setY(props.ground3dData.height);
                groundGridPlane.name = 'grid';

                props.sceneData.scene.add(groundGridPlane);
            }
        }
    }

}

const mapStateToProps = (state: FeaturesState): StateProps => {
    return {
        selectedFeatureType: state.selectedFeatureType,
        additionalSelectedFeatureTypes: state.additionalSelectedFeatureTypes,
        selectedFeatures: state.selectedFeatures,
        sceneData: state.sceneData,
        ground3dData: state.ground3dData
    };
};
const mapSceneHandlerDispatchToProps = ({});
export default connect(
    mapStateToProps,
    mapSceneHandlerDispatchToProps
)(SceneHandler);
